/**
 * The chart kit: the pieces every chart in the reports is built out of.
 *
 * ## Why there is no charting library here
 *
 * Because this repository writes its own rather than taking a dependency when the thing being
 * taken is small and the dependency is not — the listing site's ZIP encoder and its PDF writer are
 * the precedent (ADR-0092, ADR-0107), and a bar chart is a great deal less arithmetic than either.
 * A bar is a rectangle whose height is a proportion; a line is a list of points; a donut is two
 * arcs. What a library would add is three hundred kilobytes on a screen a field agent opens on a
 * slow connection, a second theming system that does not know this product's tokens, and a second
 * accessibility story that would have to be argued with rather than written.
 *
 * Written by hand, every chart inherits `--color-brand-*` and `--color-ink-*` for free, and every
 * one of them is a plain `<svg>` somebody can read in the inspector.
 *
 * ## Three rules, and they are not negotiable
 *
 * 1. **Every chart is readable without colour.** Colour is never the only thing separating two
 *    marks: a second series is textured as well as coloured, an ordered scale steps down in
 *    lightness so it survives greyscale and a monochrome printer, and anything with more than one
 *    series carries a legend whose swatch shows the same texture the mark has.
 * 2. **Every chart has a text equivalent, and it is always in the page.** `ChartFrame` renders the
 *    figures as a real `<table>`, visually hidden until somebody presses "Show the figures" and
 *    present in the accessibility tree either way. A chart is a picture of a table; a screen reader
 *    gets the table.
 * 3. **A chart never computes anything.** Every number it draws was worked out by the server and
 *    is the same number the table beside it prints. The commonest fault in a dashboard is a chart
 *    that disagrees with its own figures, and it happens when the chart re-derives a total from the
 *    rows it was handed.
 *
 * ## The palette, and where the numbers in it came from
 *
 * The four categorical colours below were checked rather than chosen by eye — lightness band,
 * chroma floor, colour-vision separation across every pair rather than only adjacent ones, and
 * contrast against the chart's own surface. A fifth hue could not be added without one pair
 * falling under the separation floor, which is why anything past four folds into a neutral
 * "Other" rather than growing a fifth colour. The ordered scales are a different job and follow a
 * different rule: they step **down in lightness**, monotonically, so the order survives with the
 * colour removed entirely.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

/* ------------------------------------------------------------------ colours */

/**
 * Four categorical hues, in a fixed order, never cycled.
 *
 * Slot one is the brand green because the first series on a page is usually the product's own
 * subject — money collected, homes sold. The other three are as far from it and from each other as
 * the checks allow. **A fifth is not added**: the pair that would have to be inserted falls below
 * the separation floor for a reader with colour-vision deficiency, and a colour two people cannot
 * tell apart is worse than a category called "Other".
 */
export const SERIES = ["#17a06e", "#2a78d6", "#eb6834", "#4a3aa7"] as const;

/** Everything past the fourth category, and never a fifth hue. */
export const OTHER = "#7d8c88";

/**
 * The arrears scale: five steps that get darker as the debt gets older.
 *
 * `current` is deliberately outside the ramp and neutral, because it is not arrears at all — it is
 * money that is not yet due, and colouring it as the mildest kind of late would say something
 * untrue about it. The four that follow fall monotonically in lightness, which is what makes the
 * order survive greyscale, a monochrome printer and a reader who sees no colour at all.
 */
export const AGEING_SCALE: Record<string, string> = {
  current: "#c4cfcd",
  d1_30: "#f59e0b",
  d31_60: "#d97706",
  d61_90: "#dc2626",
  d90_plus: "#991b1b",
};

/**
 * Named roles, so the same idea is the same colour on every report.
 *
 * A colour that means "what the agency keeps" on one screen and "reserved" on the next is a colour
 * that has to be re-learned every time, which is the same as having no colour at all. These are
 * drawn from `SERIES` and the ordered scales above rather than being new values — the point is to
 * name a slot, not to add a hue.
 *
 * The three sales states are an **ordered** set rather than a categorical one — sold, then held,
 * then still going — so they step down in lightness like the arrears scale and read in order with
 * the colour removed.
 */
export const SEGMENT_COLOURS = {
  /**
   * What the agency collects for itself.
   *
   * It was called `commission` and meant the opposite thing: a share taken out of the landlord's
   * rent. There is no such share — the agency's income is the fee the tenant pays on top, on its
   * own line of the same invoice — so the slot is named for what it holds. ADR-0131.
   */
  agency: SERIES[0],
  /** A cost the agency met and is recovering. */
  recharged: SERIES[2],
  /** What is left for the owner. */
  owners: SERIES[1],
  sold: SERIES[0],
  reserved: "#d97706",
  available: "#c4cfcd",
} as const;

/** Chart chrome. Text sits on ink tokens, never on a series colour. */
export const CHROME = {
  /** Axis and tick labels. */
  muted: "var(--color-ink-500)",
  /** Value labels and anything a reader is meant to read as a figure. */
  ink: "var(--color-ink-700)",
  /** Hairline gridlines, which must never compete with the data. */
  grid: "var(--color-ink-200)",
  /** The baseline a bar stands on. */
  axis: "var(--color-ink-300)",
  /** An empty track — the unfilled part of a meter or a composition bar. */
  track: "var(--color-ink-100)",
} as const;

/* ----------------------------------------------------------------- textures */

/**
 * The second channel, for when colour is not available.
 *
 * `solid` is the default and needs no pattern. `hatch` and `grid` are drawn over the mark's own
 * colour, so a textured series is still the colour the legend says it is — the texture is added to
 * the identity rather than replacing it. Two textures is enough: a chart needing a third is a
 * chart with too many series on it.
 */
export type Texture = "solid" | "hatch" | "grid";

/**
 * The `<defs>` every chart carries.
 *
 * The ids are namespaced with the caller's own `useId()`, because two charts on one page would
 * otherwise define the same id twice and the second one's fills would resolve to the first one's
 * pattern — which shows up as a chart that changes appearance when an unrelated card is added
 * above it.
 */
export function ChartTextures({ prefix, colours }: { prefix: string; colours: string[] }) {
  return (
    <defs>
      {colours.map((colour, index) => (
        <g key={index}>
          <pattern id={`${prefix}-hatch-${index}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill={colour} />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" strokeWidth="2.4" strokeOpacity="0.75" />
          </pattern>
          <pattern id={`${prefix}-grid-${index}`} width="5" height="5" patternUnits="userSpaceOnUse">
            <rect width="5" height="5" fill={colour} />
            <path d="M0 0H5M0 0V5" stroke="#ffffff" strokeWidth="1.4" strokeOpacity="0.8" />
          </pattern>
        </g>
      ))}
    </defs>
  );
}

/**
 * Textures for the parts of a composition, assigned by the position they are **drawn** in.
 *
 * A composition bar draws only the parts that have something in them, and which those are is a fact
 * about today's data rather than about the chart. So a texture attached to a *named* part — "the
 * thirty-to-sixty band is hatched" — is a texture that disappears on the day that band is empty,
 * taking the second channel with it and leaving a bar separated by colour alone. That is exactly
 * how a chart passes an accessibility check on one agency's books and fails on another's.
 *
 * Assigning by drawn position instead guarantees the property that actually matters: **no two
 * neighbouring parts are ever both plain**, whatever is and is not in the data. Empty parts take
 * "solid" and it is never used, so they cost the alternation nothing.
 */
export function alternatingTextures(values: number[]): Texture[] {
  let drawn = 0;
  return values.map((value) => {
    if (value <= 0) return "solid";
    return (drawn++ % 2 === 1 ? "hatch" : "solid") as Texture;
  });
}

/** What to put in a `fill`, given a colour, a texture and which slot it is. */
export function fillFor(prefix: string, index: number, colour: string, texture: Texture): string {
  if (texture === "solid") return colour;
  return `url(#${prefix}-${texture}-${index})`;
}

/* ------------------------------------------------------------- measurement */

/**
 * The width the chart actually has, in real pixels.
 *
 * Charts are not drawn into a fixed `viewBox` and scaled, because scaling scales the type with it
 * — a 12px label in a 720-wide box is six points on a 360px phone, which is not a label any more.
 * So the SVG is drawn at the size it is given, and everything inside it is positioned in device
 * pixels. The cost is this hook; the benefit is that a chart reads the same on a phone as on a
 * desk.
 *
 * Returns zero until the first measurement, and every caller must handle that by drawing nothing —
 * a chart drawn at width zero produces `NaN` co-ordinates, which SVG renders as an empty element
 * and nothing warns about.
 */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/** A stable id for one chart's `<defs>`, safe to put in a URL fragment. */
export function useChartId(): string {
  return useId().replace(/[^a-zA-Z0-9-]/g, "");
}

/* ------------------------------------------------------------------- frame */

export interface LegendEntry {
  label: string;
  colour: string;
  texture?: Texture;
  /** The figure this series is worth, printed beside its name. Optional. */
  value?: string;
}

export interface FigureTable {
  caption: string;
  columns: string[];
  rows: Array<Array<string | number>>;
}

/**
 * Everything that wraps a chart: its name, its legend, and the figures behind it.
 *
 * ## The figures are not optional and not a hover
 *
 * A tooltip is a good way to read one value and a bad way to read a chart, and it does not exist
 * at all for somebody using a keyboard or a screen reader. So every chart in this product carries
 * its numbers as a real table, always in the document, `sr-only` until somebody asks to see it.
 * `sr-only` rather than `hidden` is the whole point: `hidden` and `display: none` take content out
 * of the accessibility tree, and a table nobody can reach is not an equivalent.
 *
 * The chart itself is `aria-hidden`, with `label` describing it, because a screen reader reading
 * out a hundred `<rect>` elements is worse than silence — the table underneath is what it should
 * be reading, and it is right there.
 */
export function ChartFrame({
  label,
  legend,
  figures,
  footnote,
  series = 1,
  children,
}: {
  /** One sentence saying what the chart shows. Read instead of the marks. */
  label: string;
  legend?: LegendEntry[];
  figures: FigureTable;
  /** How a derived figure was arrived at, where that is not obvious. */
  footnote?: ReactNode;
  /**
   * How many things the drawing asks a reader to tell apart.
   *
   * Declared rather than counted, and it is what `tools/verify-reports.mjs` keys the
   * readable-without-colour rule off: **more than one and at least one mark must carry a texture**.
   * Counting it from the figures table would be wrong in both directions — a composition bar of
   * three parts has three columns and one series' worth of columns, and a line chart with a second
   * descriptive column has three columns and one series.
   */
  series?: number;
  children: (width: number) => ReactNode;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [showFigures, setShowFigures] = useState(false);
  const prefix = useChartId();

  return (
    /* `data-chart` and `data-chart-figures` are hooks for `tools/verify-reports.mjs`, which reads
       every chart's own figures out of the page and compares them against the table printed beside
       it. Class names change when a card is restyled; these do not, and a verifier keyed on styling
       is a verifier that goes quiet the first time somebody adjusts a margin. */
    <figure className="m-0" data-chart={label} data-chart-series={series}>
      <div ref={ref} className="w-full" role="img" aria-label={label}>
        {width > 0 ? children(width) : null}
      </div>

      {legend && legend.length > 1 ? <Legend entries={legend} prefix={prefix} /> : null}

      <div className="mt-3 flex items-baseline justify-between gap-3">
        {footnote ? <p className="text-xs leading-snug text-ink-500">{footnote}</p> : <span />}
        <button
          type="button"
          className="link shrink-0 text-xs"
          aria-expanded={showFigures}
          onClick={() => setShowFigures((open) => !open)}
        >
          {showFigures ? "Hide the figures" : "Show the figures"}
        </button>
      </div>

      <div className={showFigures ? "mt-2 overflow-x-auto" : "sr-only"} data-chart-figures="">
        <table className="w-full text-left text-xs">
          <caption className="sr-only">{figures.caption}</caption>
          <thead>
            <tr>
              {figures.columns.map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  className={`border-b border-ink-200 py-1.5 pr-3 font-medium text-ink-600 ${index === 0 ? "" : "text-right"}`}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {figures.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`border-b border-ink-100 py-1.5 pr-3 ${cellIndex === 0 ? "text-ink-700" : "tabular text-right text-ink-600"}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/**
 * The key.
 *
 * Present whenever there is more than one series, and never a substitute for a direct label where
 * there is room for one — a reader who has to travel to a legend and back for every mark is
 * reading an index, not a chart. The swatch carries the series' texture as well as its colour, so
 * the legend is legible in the same conditions the chart is.
 */
export function Legend({ entries, prefix }: { entries: LegendEntry[]; prefix: string }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-600">
      {entries.map((entry, index) => (
        <li key={entry.label} className="flex items-center gap-1.5">
          {/*
            The prefix carries the entry's index, and that is not tidiness.

            Every swatch is its own `<svg>` with its own `<defs>`, so a prefix shared across the
            legend defines `…-hatch-0` several times over — once per entry, each with a different
            colour. An id defined twice in one document resolves to the first, so every textured
            swatch took the *first* textured entry's colour and the legend quietly told the reader
            that amber was green. One namespace per entry.
          */}
          <svg width="11" height="11" aria-hidden="true" className="shrink-0 rounded-[2px]">
            <ChartTextures prefix={`${prefix}-legend-${index}`} colours={[entry.colour]} />
            <rect
              width="11"
              height="11"
              rx="2"
              fill={fillFor(`${prefix}-legend-${index}`, 0, entry.colour, entry.texture ?? "solid")}
              stroke="rgba(0,0,0,0.08)"
            />
          </svg>
          <span>{entry.label}</span>
          {entry.value ? <span className="tabular text-ink-500">{entry.value}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ scales */

/**
 * A rounded ceiling for an axis, so the top gridline is a number somebody would say out loud.
 *
 * Charts whose axis stops at the exact maximum give a top bar that touches the frame and a top
 * label like "47,318,904", which nobody reads.
 *
 * **The ladder is deliberately fine.** A coarse one — the usual 1, 2, 5, 10 — rounds a maximum of
 * 2.9 up to 5, which throws away two-fifths of the chart's height and flattens every other bar in
 * it by that much. The steps below are all numbers a reader divides by four in their head, which
 * is what a four-gridline axis asks of them, and none of them is more than a third above the value
 * it is rounding. Zero in means one out, because an axis from zero to zero divides by nothing.
 */
const LADDER = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

export function niceCeiling(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const scaled = max / magnitude;
  const step = LADDER.find((candidate) => scaled <= candidate) ?? 10;
  return step * magnitude;
}

/** Evenly spaced values from zero to the ceiling, for gridlines and their labels. */
export function ticksTo(ceiling: number, count = 4): number[] {
  return Array.from({ length: count + 1 }, (_, index) => (ceiling / count) * index);
}

/**
 * How many labels an axis has room for, and which ones to draw.
 *
 * Twelve month labels do not fit in 320 pixels, and the usual answer — rotate them — makes a
 * chart nobody reads either. So labels thin out: every second, then every third, always keeping
 * the first and the last, because those two are what tell a reader what period they are looking
 * at. Returns a predicate rather than a list so the caller can keep its own indexing.
 */
export function labelEvery(count: number, width: number, perLabel = 44): (index: number) => boolean {
  const room = Math.max(1, Math.floor(width / perLabel));
  const step = Math.max(1, Math.ceil(count / room));
  return (index: number) => index === 0 || index === count - 1 || index % step === 0;
}

/** Clamp a bar to at least a hairline, so a small non-zero value is visible rather than absent. */
export function visibleHeight(value: number, scale: number): number {
  if (value <= 0) return 0;
  return Math.max(2, value * scale);
}

/**
 * A callback ref that also reports the element's width, for callers that do not want the hook.
 *
 * Kept beside `useWidth` because the two are the same measurement and somebody changing one has to
 * see the other.
 */
export function useMeasuredCallback(onWidth: (width: number) => void) {
  return useCallback(
    (element: HTMLElement | null) => {
      if (element) onWidth(element.clientWidth);
    },
    [onWidth],
  );
}
